import { Injectable, signal } from '@angular/core';
import { Equipment } from '../../../models/equipment.model';
import { Mode } from '../../../models/mode.model';
import { Shop } from '../../../models/shop.model';
import { MasterDataService } from '../../../services/master-data.service';
import { SalesLocation } from '../../../models/sales-location.model';
import { RedeliveryLocation } from '../../../models/redelivery-location.model';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ToastrService } from '../../../services/toastr-service';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { EquipmentStockStatusResponse } from '../../../models/equipment-stock-status.model';

/**
 * Service to filter repair modes depending on different parameters of an equipment
 * https://maersk-tools.atlassian.net/wiki/spaces/AAEMS/pages/183034545581/Filtering+modes+based+on+Equipment+properties
 */
@Injectable()
export class ModeFilterService {
  private readonly _salesDemandState = signal<{ isBlocked: boolean; equipmentNumber: string; shopLocationCode: string }>({
    isBlocked: false,
    equipmentNumber: '',
    shopLocationCode: '',
  });
  readonly salesDemandState = this._salesDemandState.asReadonly();

  private readonly _salesDemandOverride = signal<{ overridden: boolean; reason: string }>({
    overridden: false,
    reason: '',
  });
  readonly salesDemandOverride = this._salesDemandOverride.asReadonly();

  constructor(
    private readonly masterDataService: MasterDataService,
    private readonly toastrService: ToastrService,
    private readonly appTextSvc: ApplicationtTextService,
  ) {}

  /**
   * Returns the filtered list of valid modes based on equipment and shop data.
   * @param equipment The equipment
   * @param modes List of all modes without any filtering
   * @param shop The selected maintenance shop
   * @returns Observable of valid modes
   */
  public filterModes(equipment: Equipment, modes: Mode[], shop: Shop, salesOverrideReason?: string): Observable<Mode[]> {
    const hasSalesOverrideReason = !!salesOverrideReason?.trim();

    // Reset state when equipment/shop changes
    this._salesDemandState.set({ isBlocked: false, equipmentNumber: '', shopLocationCode: '' });
    this._salesDemandOverride.set(
      hasSalesOverrideReason ? { overridden: true, reason: salesOverrideReason!.trim() } : { overridden: false, reason: '' },
    );

    // Check if the equipment is a dry container at gate-in empty move,
    // as this is the only scenario where we need to call master data for redelivery or sales locations
    const isDryContGateInEmpty =
      equipment.equipmentTypeParentCode === 'CONT' &&
      equipment.equipmentTypeCode === 'DRY' &&
      equipment.currentMove?.toUpperCase() === 'GATE-IN' &&
      (equipment.isContainerEmpty ?? false);

    // For sales demand evaluation, we only want to fetch master data
    // if the equipment is at gate-in empty move,
    // has no sales override reason,
    // and is either tagged for selling or is older than 20 years
    const shouldEvaluateSalesDemand =
      isDryContGateInEmpty &&
      !hasSalesOverrideReason &&
      ((equipment.isTaggedForSelling ?? false) || this.isEquipmentOlderThan20Years(equipment));

    const redeliveryLocations$ =
      isDryContGateInEmpty && (equipment.isTaggedForRedelivery ?? false)
        ? this.masterDataService
            .getRedeliveryLocations({
              leasingCompanyCode: equipment.leasingCompanyCode,
              leasingContractNumber: equipment.leasingContractNumber,
              equipmentOwnerType: equipment.ownershipTypeCode,
              equipmentSize: equipment.equipmentSize?.toString(),
              equipmentSubType: equipment.equipmentTypeCode,
              equipmentHeight: equipment.outsideHeight,
              poolCode: shop.locationCode,
            })
            .pipe(
              take(1),
              catchError(() => of([])),
            )
        : of([]);

    const salesLocation$ = shouldEvaluateSalesDemand
      ? this.masterDataService
          .getSalesLocation({
            locationCode: shop.locationCode,
            equipmentSize: equipment.equipmentSize?.toString() ?? null,
          })
          .pipe(
            take(1),
            catchError(() => of(null)),
          )
      : of(null);

    const stockStatus$ = shouldEvaluateSalesDemand
      ? this.masterDataService
          .getEquipmentStockStatus({
            siteCode: shop.locationCode,
            equipmentSubType: equipment.equipmentSubTypeCode ?? '',
            equipmentSize: equipment.equipmentSize?.toString() ?? '',
            equipmentHeight: equipment.outsideHeight ?? '',
            date: new Date().toISOString().split('T')[0],
          })
          .pipe(
            take(1),
            catchError(() => of(null as EquipmentStockStatusResponse | null)),
          )
      : of(null as EquipmentStockStatusResponse | null);

    return forkJoin([redeliveryLocations$, salesLocation$, stockStatus$]).pipe(
      map(([redeliveryLocations, salesLocation, stockStatus]) => {
        return this.applyModeFilters(equipment, modes, shop, redeliveryLocations, salesLocation, stockStatus);
      }),
    );
  }

  private applyModeFilters(
    equipment: Equipment,
    modes: Mode[],
    shop: Shop,
    redeliveryLocations: RedeliveryLocation[],
    salesLocation: SalesLocation | null,
    stockStatus: EquipmentStockStatusResponse | null,
  ): Mode[] {
    let validModes: string[] = [];

    if (this.isRedeliveryContainer(equipment, shop)) {
      validModes = this.addRedeliveryModes(equipment);
    } else if (this.isRedeliveryDemandContainer(redeliveryLocations)) {
      validModes = this.addRedeliveryModes(equipment);
      this.toastrService.showToastr(this.appTextSvc.RedeliveryDemandContainerWarning, 'warning', { duration: 60000 });
    } else if (this.isSalesDemandContainer(equipment, salesLocation, stockStatus)) {
      validModes = this.addSalesModes(equipment);
      this._salesDemandState.set({
        isBlocked: true,
        equipmentNumber: equipment.equipmentNumber ?? '',
        shopLocationCode: shop.locationCode ?? '',
      });
    } else if (this.isNeutralizationContainer(equipment, shop)) {
      validModes = this.addNeutralizationModes(equipment);
    } else if (this.isNormalActiveContainer(equipment, shop)) {
      validModes = this.addModesBasedOnEquipmnentTypes(equipment);
    } else if (equipment.equipmentTypeParentCode === 'GENS' && !this.isRedeliveryShop(shop)) {
      validModes = this.addGensetModes();
    } else if (this.isActivePowerPack(equipment, shop)) {
      validModes = this.addPowerPackModes();
    }

    return modes.filter((mode) => validModes.includes(mode.code));
  }

  private isRedeliveryContainer(equipment: Equipment, shop: Shop): boolean {
    return (
      equipment.equipmentTypeParentCode === 'CONT' &&
      (equipment.isTaggedForRedelivery ?? false) &&
      this.isRedeliveryShop(shop) &&
      equipment.equipmentIsActive === false
    );
  }

  private isNeutralizationContainer(equipment: Equipment, shop: Shop): boolean {
    const totalLossDamageCode: number = 1;
    return (
      equipment.equipmentTypeParentCode === 'CONT' && equipment.damageCode === totalLossDamageCode && this.isRedeliveryShop(shop) === false
    );
  }

  private isNormalActiveContainer(equipment: Equipment, shop: Shop): boolean {
    return equipment.equipmentTypeParentCode === 'CONT' && (equipment.equipmentIsActive ?? false) && this.isRedeliveryShop(shop) === false;
  }

  /**
   * Identifies if the equipment is an active power pack,
   * which is defined as a power pack currently at gate-in empty move
   * and located at a location that is not a redelivery shop.
   * @param equipment Equipment details
   * @param shop Shop details
   * @returns True if the equipment is an active power pack, false otherwise
   */
  private isActivePowerPack(equipment: Equipment, shop: Shop): boolean {
    return equipment.equipmentTypeParentCode === 'PWPK' && (equipment.equipmentIsActive ?? false) && this.isRedeliveryShop(shop) === false;
  }

  /**
   * Identifies if the equipment is a redelivery demand container,
   * which is defined as a container tagged for redelivery, currently at gate-in empty move,
   * and matched to a redelivery location configuration with WO mode blocking enabled.
   * @param redeliveryLocations Redelivery locations matching the configuration criteria for the equipment and location
   * @returns True if the equipment is a redelivery demand container, false otherwise
   */
  private isRedeliveryDemandContainer(redeliveryLocations: RedeliveryLocation[]): boolean {
    return redeliveryLocations.some((rl) => rl.enableWoModeBlocking === true);
  }

  /**
   * Identifies if the equipment is a sales demand container,
   * which is defined as a container currently at gate-in empty move
   * and located at a location with sales demand and stock status indicating surplus or balanced.
   * @param equipment Equipment details
   * @param salesLocation Sales location data for the current shop location
   * @param stockStatus Stock status data for the equipment type and location, used to determine if site has surplus or deficit amount of equipments
   * @returns True if the equipment is a sales demand container, false otherwise
   */
  private isSalesDemandContainer(
    equipment: Equipment,
    salesLocation: SalesLocation | null,
    stockStatus: EquipmentStockStatusResponse | null,
  ): boolean {
    if (!salesLocation?.enableWoModeBlocking) {
      return false;
    }

    if (!stockStatus?.siteStatus || stockStatus.siteStatus.toUpperCase() === 'DEFICIT') {
      // If stock status is DEFICIT or not available, treat as normal container (unblocked)
      return false;
    }

    if (salesLocation.isBonusLocation) {
      return this.isEquipmentOlderThan20Years(equipment);
    }

    return equipment.isTaggedForSelling ?? false;
  }

  public isRedeliveryShop(shop: Shop): boolean {
    return shop.locationCode?.toUpperCase().startsWith('NEAGA') === true;
  }

  private addRedeliveryModes(equipment: Equipment): string[] {
    let modeCodes: string[] = [];
    switch (equipment.equipmentTypeCode) {
      case 'REEF':
        modeCodes.push('22', '27', '80', '82');
        break;
      case 'DRY':
        modeCodes.push('23', '83');
        break;
      case 'PLWD':
      case 'BULK':
      case 'HIVE':
        modeCodes.push('83');
        break;
      case 'FLAT':
      case 'TWDK':
      case 'TWFL':
        modeCodes.push('84');
        break;
      case 'OPEN':
        modeCodes.push('85');
        break;
    }
    return modeCodes;
  }

  private addNeutralizationModes(equipment: Equipment): string[] {
    let modeCodes: string[] = [];
    switch (equipment.equipmentTypeCode) {
      case 'REEF':
        modeCodes.push('22', '26', '27', '49', '70');
        break;
      case 'DRY':
      case 'OPEN':
      case 'FLAT':
      case 'BULK':
      case 'HIVE':
      case 'PLWD':
      case 'TWDK':
      case 'TWFL':
        modeCodes.push('23', '70');
        break;
    }
    return modeCodes;
  }

  private addModesBasedOnEquipmnentTypes(equipment: Equipment): string[] {
    let modeCodes: string[] = [];
    modeCodes.push('70');
    switch (equipment.equipmentTypeCode) {
      case 'REEF':
        modeCodes.push('02', '22', '26', '27', '40', '41', '43', '48', '49');
        if (this.isControlledAtmosphereContainer(equipment)) {
          modeCodes.push('45', '46');
        }
        break;
      case 'DRY':
      case 'HIVE':
      case 'BULK':
      case 'PLWD':
        modeCodes.push('23', '03', '93');
        break;
      case 'FLAT':
      case 'TWDK':
      case 'TWFL':
        modeCodes.push('23', '04');
        break;
      case 'OPEN':
        modeCodes.push('23', '05');
        break;
      case 'TANK':
        modeCodes.push('10');
        break;
    }
    return modeCodes;
  }

  private addGensetModes() {
    return ['26', '31', '33', '70', '71'];
  }

  private addPowerPackModes() {
    return ['70'];
  }

  /**
   * Returns modes for sales demand containers.
   * @param equipment Equipment details
   * @returns Modes for sales demand containers
   */
  private addSalesModes(equipment: Equipment): string[] {
    let modeCodes: string[] = [];
    if (equipment.equipmentTypeCode === 'DRY') {
      modeCodes.push('23');
    }
    return modeCodes;
  }

  private isSteelContainer(equipment: Equipment) {
    return ['SST', 'STL'].includes(equipment.constructionMaterialCode ?? '');
  }

  private isControlledAtmosphereContainer(equipment: Equipment): boolean {
    if (['CARP', 'CASC', 'CARF'].includes(equipment.equipmentSubTypeCode ?? '')) {
      return true;
    } else {
      return false;
    }
  }

  private isMaerskOwned(equipment: Equipment): boolean {
    const isOwned = equipment.ownershipTypeCode === 'OWN';
    const maerskSubsidiaryCompanyCode = ['CPH', 'SUD', 'SCL', 'HBD', 'SEN', 'PON', 'MSC', 'MSK'];
    const isLeasedByMaerskSubsidiary =
      ['LT', 'ST'].includes(equipment.ownershipTypeCode ?? '') && maerskSubsidiaryCompanyCode.includes(equipment.leasingCompanyCode || '');

    return isOwned || isLeasedByMaerskSubsidiary;
  }

  /**
   * Checks if the equipment is older than 20 years.
   * @param equipment Equipment details
   * @returns True if the equipment is older than 20 years, otherwise false
   */
  private isEquipmentOlderThan20Years(equipment: Equipment): boolean {
    if (!equipment.manufacturingDateTime || equipment.manufacturingDateTime === '0001-01-01T00:00:00Z') {
      return false;
    }
    const twentyYearsAgo = new Date();
    twentyYearsAgo.setFullYear(twentyYearsAgo.getFullYear() - 20);
    return new Date(equipment.manufacturingDateTime) < twentyYearsAgo;
  }
}
